import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchPrescriptionViewSettings } from 'src/clinic/reducers/prescription/services/prescriptionViewSettingsThunk'
import usePrescriptionViewSettingsForRx from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/hooks/usePrescriptionViewSettingsForRx'
import {
  getInitialDataForSettingDaysOfNextVisit,
  getInitialDataForSettingExistingDrugAdvice,
  getInitialDataForSettingExistingSelectedAdvice,
  getInitialDataForSettingExistingSelectedComplaints,
  getInitialDataForSettingExistingSelectedDiagnosis,
  getInitialDataForSettingExistingSelectedInstructions,
  getInitialDataForSettingExistingSelectedInvestigations,
  getInitialDataForSettingExistingSelectedObservations,
  getInitialDataForSettingExistingSelectedTherapies,
  getInitialDataForSettingExistingHealthEntity,
} from '../PrescriptionMakingPageComponents'
import PrescriptionViewComponent from '../printComponents/PrescriptionViewComponent'
import A4PrintSettings from '../printComponents/printSettings/A4PrintSettings'
import { getFreeFormRichTextFromEmrSectionDataList } from 'src/mentor/components/EmrRelatedComponents/FreeFormRichText/freeFormRichTextUtils'
import { EMR_SECTION_IDS } from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/emrSectionIds'

export default function ViewOnlyPrescriptionV2({
  prescriptionData,
  prescriptionViewSettings: propPrescriptionViewSettings,
  doctorEmrSections,
  allVitalsOfThisPatient,
  useOldUI = false,
  forAssistant = false,
  oldUIDrugSubSections,
  hidePrintMarginsControl = true,
  useFullUrlForHeaderImages = false,
  useFullUrlForFooterImages = false,
  showTheA4Page = false,
  useEmptyBox = false,
  useBestFit = false,
  practitionerId, // Optional: required for support portal
}) {
  const dispatch = useDispatch()
  const historyPrescriptionData = prescriptionData?.prescriptionData || {}
  
  // Get the latest settings from Redux store to ensure we have all properties including drugAdviceTablePrintType
  const prescrpitionViewSettingsStoreData = useSelector(selectPrescriptionViewSettingsData)
  const storeSettings = prescrpitionViewSettingsStoreData?.data
  
  // Fetch settings from API if not in store. Only fire on a fresh state — if
  // we already attempted and got 'loading' / 'error' / 'success', don't
  // re-dispatch on every render. Without this guard, a 403 (e.g. user lacks
  // ROLE_TEACHER) flips state to 'error' which changes the dep, the effect
  // re-runs, the thunk fires again, and the cycle repeats indefinitely.
  useEffect(() => {
    const fetchState = prescrpitionViewSettingsStoreData?.state
    const shouldFetch = !storeSettings && (fetchState == null || fetchState === 'idle')
    if (shouldFetch) {
      dispatch(fetchPrescriptionViewSettings({
        emrReportType: 'prescription',
        practitionerId, // Pass practitionerId for support portal
      }))
    }
  }, [storeSettings, prescrpitionViewSettingsStoreData?.state, dispatch, practitionerId])
  
  // Prioritize store settings if available, otherwise fall back to prop settings
  // Store settings should always be the source of truth for drugAdviceTablePrintType
  const prescriptionViewSettings = storeSettings 
    ? {
        // Start with store settings as base (most up-to-date)
        ...storeSettings,
        // Merge in prop settings, but store settings take priority for critical fields
        ...propPrescriptionViewSettings,
        // CRITICAL: Ensure drugAdviceTablePrintType always comes from store if available
        drugAdviceTablePrintType: storeSettings?.drugAdviceTablePrintType,
        // For nested objects, merge them properly
        ...(propPrescriptionViewSettings?.paperPrintSettings && {
          paperPrintSettings: {
            ...storeSettings?.paperPrintSettings,
            ...propPrescriptionViewSettings.paperPrintSettings,
          },
        }),
        ...(propPrescriptionViewSettings?.pdfPrintSettings && {
          pdfPrintSettings: {
            ...storeSettings?.pdfPrintSettings,
            ...propPrescriptionViewSettings.pdfPrintSettings,
          },
        }),
      }
    : propPrescriptionViewSettings || {}
  
  const { printSettings, setPrintSettings } = usePrescriptionViewSettingsForRx({
    metaPrescription: prescriptionData,
    prescriptionViewSettings: prescriptionViewSettings,
  })

  const freeFormFromPrescription = prescriptionData?.prescriptionData?.freeFormRichText || ''
  const freeFormFromEmrSectionData = getFreeFormRichTextFromEmrSectionDataList(
    prescriptionData?.emrSectionDataList ||
    prescriptionData?.prescriptionData?.emrSectionDataList ||
    []
  )
  const resolvedFreeFormRichText = freeFormFromPrescription || freeFormFromEmrSectionData || ''
  const freeFormSectionConfig = Array.isArray(doctorEmrSections)
    ? doctorEmrSections.find((s) => s?.id === 'free_form_rich_text')
    : null

  const selectedObservationsData = getInitialDataForSettingExistingHealthEntity(
    historyPrescriptionData?.healthObservations,
    historyPrescriptionData?.symptomsAndObservations,
  )
  const selectedComplaintsData = getInitialDataForSettingExistingSelectedComplaints(
    historyPrescriptionData?.complaints,
  )
  const selectedDiagnosisData = getInitialDataForSettingExistingSelectedDiagnosis(
    historyPrescriptionData?.diagnosis,
  )
  const selectedAdviceData = getInitialDataForSettingExistingHealthEntity(
    historyPrescriptionData?.prescriptionAdvices,
    historyPrescriptionData?.guidanceAndAdvices,
  )
  const selectedTherapiesData = getInitialDataForSettingExistingSelectedTherapies(
    historyPrescriptionData?.therapiesNeeded,
  )
  const selectedInvestigationsData = getInitialDataForSettingExistingSelectedInvestigations(
    historyPrescriptionData?.investigations,
  )
  const selectedInstructionsData = getInitialDataForSettingExistingHealthEntity(
    historyPrescriptionData?.healthInstructions,
    historyPrescriptionData?.instructions,
  )
  const selectedAssessmentsData = getInitialDataForSettingExistingHealthEntity(
    historyPrescriptionData?.prescribedAssessments,
    historyPrescriptionData?.psychAssessments,
  )
  // The therapy-recommended section renders when therapies are suggested; mirror the
  // editor which flips this on whenever therapies exist (or the record carries the flag).
  const suggestedBookingTherapyData =
    Boolean(historyPrescriptionData?.suggestedBookingTherapy) || selectedTherapiesData.length > 0
  // Dummy/template previews have no separate patient-vitals fetch (allVitalsOfThisPatient is
  // empty); the dummy GET returns the vital under patientPrefill.vital (same place age/gender
  // come from), so fall back to it.
  const resolvedVital =
    allVitalsOfThisPatient?.find(
      (item) => item?.bookedEventId === historyPrescriptionData?.bookedEventId,
    ) ||
    prescriptionData?.patientPrefill?.vital ||
    historyPrescriptionData?.vital ||
    prescriptionData?.vital ||
    null
  const drugsAdviceData = getInitialDataForSettingExistingDrugAdvice(
    historyPrescriptionData?.drugInfoList,
  )

  const prescriptionSnapshotEmrSections =
    historyPrescriptionData?.emrSections || prescriptionData?.emrSections

  const isHistoryPrescriptionRecord = prescriptionData?.apiParam === 'meta-prescription'

  // Fallback section layout for prescriptions that carry data but no EMR section layout (e.g.
  // bulk-imported records, or doctors without a configured template). Without any sections the
  // section-driven renderer shows only the header/footer; here the data-bearing sections below get
  // marked selected/printable by sectionHasDataMap so the content actually renders.
  const DEFAULT_DATA_BEARING_SECTIONS = [
    { id: EMR_SECTION_IDS.HEALTH_COMPLAINTS, title: 'Complaints' },
    { id: EMR_SECTION_IDS.DIAGNOSIS, title: 'Diagnosis' },
    { id: EMR_SECTION_IDS.OBSERVATIONS, title: 'Observations' },
    { id: EMR_SECTION_IDS.DRUG_ADVICE, title: 'Medications' },
    { id: EMR_SECTION_IDS.INVESTIGATIONS, title: 'Investigations' },
    { id: EMR_SECTION_IDS.ADVICE, title: 'Advice' },
    { id: EMR_SECTION_IDS.INSTRUCTIONS, title: 'Instructions' },
    { id: EMR_SECTION_IDS.FREE_FORM_RICH_TEXT, title: 'Notes' },
  ]

  const resolvedBaseEmrSections =
    Array.isArray(prescriptionSnapshotEmrSections) && prescriptionSnapshotEmrSections.length > 0
      ? prescriptionSnapshotEmrSections
      : Array.isArray(doctorEmrSections) && doctorEmrSections.length > 0
        ? doctorEmrSections
        : []

  const baseEmrSections =
    resolvedBaseEmrSections.length > 0 ? resolvedBaseEmrSections : DEFAULT_DATA_BEARING_SECTIONS

  // History prescriptions should use the modern paginated renderer path,
  // because old UI path does not reliably render all prescriptionData fields.
  const shouldUseOldUI = Boolean(useOldUI) && !isHistoryPrescriptionRecord

  const sectionHasDataMap = {
    [EMR_SECTION_IDS.FREE_FORM_RICH_TEXT]: (resolvedFreeFormRichText || '').trim().length > 0,
    [EMR_SECTION_IDS.OBSERVATIONS]: selectedObservationsData.length > 0,
    [EMR_SECTION_IDS.HEALTH_COMPLAINTS]: selectedComplaintsData.length > 0,
    [EMR_SECTION_IDS.DIAGNOSIS]: selectedDiagnosisData.length > 0,
    [EMR_SECTION_IDS.DRUG_ADVICE]: drugsAdviceData.length > 0,
    [EMR_SECTION_IDS.ADVICE]: selectedAdviceData.length > 0,
    [EMR_SECTION_IDS.INVESTIGATIONS]: selectedInvestigationsData.length > 0,
    [EMR_SECTION_IDS.THERAPY_RECOMMENDATION]: suggestedBookingTherapyData,
    [EMR_SECTION_IDS.INSTRUCTIONS]: selectedInstructionsData.length > 0,
    [EMR_SECTION_IDS.PSYCH_ASSESSMENTS]: selectedAssessmentsData.length > 0,
    [EMR_SECTION_IDS.PATIENT_VITALS]: Boolean(resolvedVital),
  }

  const effectiveEmrSections = baseEmrSections.map((section) => {
    const sectionId = section?.id
    if (!sectionId) return section
    if (isHistoryPrescriptionRecord && sectionHasDataMap[sectionId]) {
      return {
        ...section,
        selected: true,
        printable: true,
      }
    }
    if (!sectionHasDataMap[sectionId]) return section
    return {
      ...section,
      selected: true,
      printable: true,
    }
  })

  const historySectionStatusMap = effectiveEmrSections.reduce(
    (acc, item) => {
      acc[item?.id] = { selected: item?.selected ?? null, printable: item?.printable ?? null }
      return acc
    },
    {},
  )

  function setAndStoreRelevantPrintSettingsInLocalStorage(prevOrObj) {
    setPrintSettings((prev) => {
      let finalFormedObject = prev

      // Determine the final formed object
      if (typeof prevOrObj === 'function') {
        finalFormedObject = prevOrObj(prev)
      } else {
        finalFormedObject = prevOrObj
      }

      // Extract relevant fields without destructuring
      const printSettings = {}
      printSettings.topMargin = finalFormedObject?.topMargin ?? 0
      printSettings.bottomMargin = finalFormedObject?.bottomMargin ?? 0
      printSettings.leftMargin = finalFormedObject?.leftMargin ?? 0
      printSettings.rightMargin = finalFormedObject?.rightMargin ?? 0

      // Store in localStorage
      localStorage.setItem('printSettings', JSON.stringify(printSettings))

      // Return the final formed object
      return finalFormedObject
    })
  }

  // function setInitialPrintSettings() {
  //   const printSettingsFromLocalStorage = JSON.parse(localStorage.getItem('printSettings')) || {}

  //   const fHeight = isValidNumber(prescriptionData?.footer?.customFooterHeight)
  //     ? prescriptionData?.footer?.customFooterHeight
  //     : prescriptionViewSettings?.paperPrintSettings?.footerSettings?.footerHeight

  //   const hHeight = isValidNumber(prescriptionData?.header?.customHeaderHeight)
  //     ? prescriptionData?.header?.customHeaderHeight
  //     : prescriptionViewSettings?.paperPrintSettings?.headerSettings?.headerHeight

  //   const lSbarWidth = isValidNumber(prescriptionData?.leftSidebar?.width)
  //     ? prescriptionData?.leftSidebar?.width
  //     : prescriptionViewSettings?.paperPrintSettings?.leftSidebarSettings?.width

  //   const rSbarWidth = isValidNumber(prescriptionData?.rightSidebar?.width)
  //     ? prescriptionData?.rightSidebar?.width
  //     : prescriptionViewSettings?.paperPrintSettings?.rightSidebarSettings?.width

  //   const topMargin = isValidNumber(prescriptionData?.margins?.marginTop)
  //     ? prescriptionData?.margins?.marginTop
  //     : prescriptionViewSettings?.paperPrintSettings?.marginTop
  //   const bottomMargin = isValidNumber(prescriptionData?.margins?.marginBottom)
  //     ? prescriptionData?.margins?.marginBottom
  //     : prescriptionViewSettings?.paperPrintSettings?.marginBottom
  //   const leftMargin = isValidNumber(prescriptionData?.margins?.marginLeft)
  //     ? prescriptionData?.margins?.marginLeft
  //     : prescriptionViewSettings?.paperPrintSettings?.marginLeft
  //   const rightMargin = isValidNumber(prescriptionData?.margins?.marginRight)
  //     ? prescriptionData?.margins?.marginRight
  //     : prescriptionViewSettings?.paperPrintSettings?.marginRight

  //   const printHeaderType =
  //     prescriptionData?.header?.customHeaderType ||
  //     prescriptionViewSettings?.paperPrintSettings?.headerSettings?.headerStyle

  //   const printFooterType =
  //     prescriptionData?.footer?.customFooterType ||
  //     prescriptionViewSettings?.paperPrintSettings?.footerSettings?.showBlankFooter

  //   setPrintSettings((prev) => ({
  //     ...prev,

  //     useOldPrintMethod:
  //       useOldUI || !prescriptionViewSettings?.paperPrintSettings?.useNewPrintMethod,

  //     topMargin: printSettingsFromLocalStorage?.topMargin ?? topMargin ?? 0,
  //     bottomMargin: printSettingsFromLocalStorage?.bottomMargin ?? bottomMargin ?? 0,
  //     leftMargin: printSettingsFromLocalStorage?.leftMargin ?? leftMargin ?? 0,
  //     rightMargin: printSettingsFromLocalStorage?.rightMargin ?? rightMargin ?? 0,

  //     headerHeight: hHeight ?? 0,
  //     pdfHeaderHeight:
  //       prescriptionData?.header?.customHeaderHeight ??
  //       prescriptionViewSettings?.pdfPrintSettings?.headerSettings?.headerHeight,
  //     pdfHeaderType: prescriptionViewSettings?.pdfPrintSettings?.headerSettings?.headerStyle,
  //     printHeaderType: printHeaderType,
  //     blankHeader: false,

  //     leftSidebarWidth: lSbarWidth ?? 0,
  //     rightSidebarWidth: rSbarWidth ?? 0,

  //     footerHeight: fHeight ?? 0,
  //     printFooterType: printFooterType,
  //     blankFooter: false,
  //     // blankFooter: prescriptionViewSettings?.paperPrintSettings?.footerSettings?.showBlankFooter,
  //   }))
  // }

  // console.log({
  //   // requestedMntr: prescriptionData?.prescriptionData?.referredToDoctorName,
  //   // testsRequestedBy: prescriptionData?.prescriptionData?.finishRequestedTestsBy,
  //   // allVitalsOfThisPatient,
  //   // prescriptionData,
  //   prescriptionViewSettings,
  // })

  return (
    <div className="d-flex flex-column gap-2">
      {!printSettings?.useOldPrintMethod && !hidePrintMarginsControl && (
        <div className="mr-2 hide-on-print-only">
          <div className="mx-3 hide-on-print-only" style={{ flex: 1 }}>
            <A4PrintSettings
              printSettings={printSettings}
              setPrintSettings={setAndStoreRelevantPrintSettingsInLocalStorage}
            />
          </div>
        </div>
      )}
      <PrescriptionViewComponent
        prescriptionTemplateData={prescriptionData}
        showInUI
        selectedObservations={selectedObservationsData}
        selectedComplaints={selectedComplaintsData}
        selectedDiagnosis={selectedDiagnosisData}
        drugsAdvice={drugsAdviceData}
        prescriptionData={prescriptionData}
        selectedAdvice={selectedAdviceData}
        selectedTherapies={selectedTherapiesData}
        nextVisitInputType="days"
        nextVisitInputValue={getInitialDataForSettingDaysOfNextVisit(
          historyPrescriptionData?.nextVisitDate,
          historyPrescriptionData?.createdTime,
        )}
        selectedInvestigations={selectedInvestigationsData}
        selectedInstructions={selectedInstructionsData}
        selectedAssessments={selectedAssessmentsData}
        suggestedBookingTherapy={suggestedBookingTherapyData}
        useOldUI={shouldUseOldUI ? shouldUseOldUI : printSettings?.useOldPrintMethod}
        finalRequestedTestsBy={historyPrescriptionData?.finishRequestedTestsBy}
        referredToDoctorName={historyPrescriptionData?.referredToDoctorName}
        vital={resolvedVital}
        value=""
        emrSections={effectiveEmrSections}
        practitionerId={practitionerId}
        oldUIDrugSubSections={oldUIDrugSubSections}
        setValue={() => null}
        setPropValue={() => null}
        prescriptionViewSettings={prescriptionViewSettings}
        honorPropValue={false}
        freeFormRichText={resolvedFreeFormRichText}
        printSettings={printSettings}
        refreshDimensionsDependencies={[printSettings, prescriptionData, prescriptionViewSettings]}
        useFullUrlForHeaderImages={useFullUrlForHeaderImages}
        useFullUrlForFooterImages={useFullUrlForFooterImages}
        showTheA4Page={showTheA4Page}
        useEmptyBox={useEmptyBox}
        useBestFit={useBestFit}
      />
    </div>
  )
}
